import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Col, Row } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CompletePopup from './completepopup';
import NoInternet from '../NoInternet';
import NoDataFound2 from '../NoData2';
import { useSnackbar } from "../context/SnackbarContext";

// img
import RotatePhonePopup from '../RotatePhonePopup';
import Nouser from "../../img/NoUserFound.png";
import NoNet from "../../img/NoNet.png";
import NouserDark from "../../img/NoUserFoundDark.png";
import NoNetDark from "../../img/NoNetDark.png";
import LanguageDropdown from '../Languagedropdown';
import { useLanguage } from '../context/Languagecontext';
import { useTranslatedText } from '../../hooks/useTranslatedText';
import { convertToNativeNumerals } from '../../utils/numberUtils';
import DiceButton from './DiceButton';
import useBadWordDetection from '../../hooks/Usebadworddetection';
import BadWordWarning from './Badwordwarning';
import useCardPreview from '../../hooks/useCardPreview';

const isSmallScreen = window.innerWidth <= 375 && window.innerWidth !== 360;

// SessionStorage helper functions
const saveToSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error('Error saving to sessionStorage:', error);
    }
};

// Skeleton Loader Component
// Updated Skeleton Loader Component
const CardSkeleton = ({ width }) => (
    <div
        className="shadow rounded-4 mx-auto p-0 d-flex flex-column justify-content-between position-relative overflow-hidden"
        style={{
            border: "3px solid rgba(0, 0, 0, 0.1)",
            width: width,
            minHeight: isSmallScreen ? "400px" : "480px",
        }}
    >
        <div
            className="shimmer-element"
            style={{
                height: "90px",
                width: "100%",
            }}
        >
        </div>

        <div
            className="shimmer-element mx-auto"
            style={{
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "120px",
                height: "120px",
            }}
        />

        {/* Comment Box Section Skeleton */}
        <div className='d-flex align-items-center justify-content-center'>
            <div
                className='mx-auto rounded-4 shimmer-element'
                style={{
                    width: "90%",
                    minHeight: "90px",
                    border: "2px solid rgba(0, 0, 0, 0.1)",
                    marginBottom: "16px"
                }}
            >
            </div>
        </div>
    </div>
);

const getInitialDarkMode = () => {
    if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
};


const FriendCard = () => {
    const { lanText, t } = useLanguage();
    const location = useLocation();
    const { username, category } = useParams();
    const navigate = useNavigate();


    // FIXED: Prioritize fresh data from location.state over sessionStorage
    const getStateData = () => {
        // First priority: fresh navigation data
        if (location.state && Object.keys(location.state).length > 0) {
            sessionStorage.setItem('friendFlowData', JSON.stringify(location.state));
            return location.state;
        }

        // Second priority: session storage
        const sessionData = sessionStorage.getItem('friendFlowData');
        if (sessionData) {
            try {
                return JSON.parse(sessionData);
            } catch (error) {
                console.error('Error parsing session data:', error);
                return null;
            }
        }

        return null;
    };

    const stateData = getStateData();

    const { hint, uploadedImage, originalData, questionText, name, image, bg, content, subCat, subCategory } = stateData;
    const translatedQuestion = useTranslatedText(questionText, lanText);
    const translatedName = useTranslatedText(name, lanText);

    // Check if success modal should be shown on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowModal = urlParams.get("success") === "1";

    const { loading, randomData, imageLoaded, noInternet, error } = useCardPreview({
        type: "friend",
        shouldShowModal,
    });

    
    const [isUploading, setIsUploading] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(shouldShowModal);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [noNetImgSrc, setNoNetImgSrc] = useState(null);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noUserImgLoaded, setNoUserImgLoaded] = useState(false);
    const [noUserImgSrc, setNoUserImgSrc] = useState(null);
    const [textareaClicked, setTextareaClicked] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [comment, setComment] = useState(content || '');
    const { hasBadWord } = useBadWordDetection(comment);
    const textareaRef = useRef(null);
    const mainContainerRef = useRef(null);
    const topRef = useRef(null);
    const originalContentRef = useRef(stateData?.content || '');


    const { showSnackbar } = useSnackbar();

    const cardWidth = () => {
        if (window.innerWidth <= 375) return '93%';
        return '360px';
    };


    const isInAppBrowser = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return /Instagram|FBAN|FBAV|Messenger|Telegram|FxiOS|Firefox|OPR|Opera|Brave/i.test(ua);
    };

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!navigator.onLine) {
            return false;
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };

    // Check dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(darkModeMediaQuery.matches);
        };

        checkDarkMode();

        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => setIsDarkMode(e.matches);

        darkModeMediaQuery.addEventListener('change', handleChange);

        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    const currentNoUserImg = isDarkMode ? noUserDarkImgSrc : noUserImgSrc;
    const currentNoNetImg = isDarkMode ? noNetDarkImgSrc : noNetImgSrc;

    // Load images from localStorage (written by CategoryLink)
    useEffect(() => {
        let isMounted = true;

        const loadImage = (key, fallbackImg, setter, setLoaded) => {
            const cached = localStorage.getItem(key);
            if (isMounted) {
                setter(cached || fallbackImg);
                if (setLoaded) setLoaded(true);
            }
        };

        loadImage('noUserImage', Nouser, setNoUserImgSrc, setNoUserImgLoaded);
        loadImage('noNetImage', NoNet, setNoNetImgSrc, setNoNetImgLoaded);
        loadImage('noUserImageDark', NouserDark, setNoUserDarkImgSrc);
        loadImage('noNetImageDark', NoNetDark, setNoNetDarkImgSrc);

        return () => { isMounted = false; }
    }, []);

    // Save location.state to sessionStorage on component mount
    useEffect(() => {
        if (location.state) {
            saveToSessionStorage('friendCardState', location.state);
        }
    }, [location.state]);


    // Handle back button when modal is open
    useEffect(() => {
        const handlePopState = () => {
            if (showSuccessModal) {
                setShowSuccessModal(false);
                const url = new URL(window.location.href);
                if (url.searchParams.has("success")) {
                    url.searchParams.delete("success");
                    window.history.replaceState({}, "", url.pathname);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showSuccessModal]);

    // Check URL params for success modal
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get("success") === "1") {
            setShowSuccessModal(true);
        }
    }, []);

    useEffect(() => {
        if (!stateData || !stateData.questionText || !stateData.originalData) {
            console.log('Missing essential data, redirecting to start...');
            navigate(`/${username}/${category}`);
        }
    }, [stateData, username, category, navigate]);

    useEffect(() => {
        const isDisabled = isUploading || isCheckingConnection || textareaClicked;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isUploading, isCheckingConnection, textareaClicked]);

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };


    const upload = async () => {
        const deviceIp = localStorage.getItem("deviceIp");
        setIsUploading(true);

        try {
            const formData = new FormData();

            formData.append('id', originalData.id);
            formData.append('category', subCat);
            formData.append("webCategory", category);
            formData.append('hint', hint);

            if (uploadedImage) {
                if (uploadedImage.startsWith('data:image')) {
                    formData.append('uploadedImage', base64ToFile(uploadedImage, 'hintImage.jpg'));
                } else {
                    formData.append('uploadedImage', uploadedImage);
                }
            }
            formData.append('friendBg', randomData.CardBg);
            formData.append('friendEmoji', image);
            formData.append('friendName', translatedName);
            formData.append('friendContent', comment);
            formData.append('userLocation', "allow");
            formData.append('deviceIp', deviceIp);

            await axios.post('https://api.lolcards.link/api/newinbox/create', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const url = new URL(window.location.href);
            url.searchParams.set('success', '1');
            window.history.pushState({ success: true }, '', url.toString());
            setShowSuccessModal(true);
            const currentData = JSON.parse(sessionStorage.getItem('friendFlowData') || '{}');
            currentData.content = '';
            sessionStorage.setItem('friendFlowData', JSON.stringify(currentData));
            setComment('')
            sessionStorage.removeItem('category7_activeIndex');
            sessionStorage.removeItem('category7_tabContents');
        } catch (error) {
            console.error('Error uploading data:', error);
            // const errorMessage = error.response?.data?.message || 'Something went wrong while uploading.';
            showSnackbar(t?.common?.apiFail || 'Request failed. Please try again after some time!', "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendClick = async () => {
        if (!isUploading) {
            setIsCheckingConnection(true);

            const isOnline = await checkInternetConnection();

            setIsCheckingConnection(false);

            if (!isOnline) {
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");

                return;
            }
            try {
                await upload();
                await axios.patch(
                    'https://api.lolcards.link/api/admin/monitoring',
                    { categoryname: category, id: originalData.id }
                );
            } catch (error) {
                console.error('Error updating monitoring:', error);
            }
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        // Clear all emotion flow data when successfully completed
        // sessionStorage.removeItem('emotionFlowData');
        // sessionStorage.removeItem('audioBlob');

        // Clear URL params
        const url = new URL(window.location.href);
        if (url.searchParams.has("success")) {
            url.searchParams.delete("success");
            window.history.replaceState({}, "", url.pathname);
        }
    };

    const handleKeyDown = (e) => {
        // On mobile keyboards, when user presses the "Done" button
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default behavior (new line)

            // Blur the textarea to hide the keyboard
            e.target.blur();
        }
    };

    // 5. Update your handleTextareaClick function:
    const handleTextareaClick = () => {
        setTextareaClicked(true);
        setTimeout(() => {
            if (textareaRef.current) {
                const length = textareaRef.current.value.length;
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(length, length);
            }
        }, 0);
    };

    const maxLength = 150;
    const typedChars = [...comment].length;


    // Trigger shake animation - Added shake functionality
    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const handleTextChange = (e) => {
        const newValue = e.target.value;
        const charCount = [...newValue].length;

        if (charCount > maxLength) {
            triggerShake();
            return;
        }

        setComment(newValue);

        // Update friendFlowData session
        const currentData = JSON.parse(sessionStorage.getItem('friendFlowData') || '{}');
        const updatedData = { ...currentData, content: newValue };
        sessionStorage.setItem('friendFlowData', JSON.stringify(updatedData));

        //  Also update category7_tabContents so Category7 screen reflects the change
        const activeIndex = parseInt(sessionStorage.getItem('category7_activeIndex') || '0');
        const tabContents = JSON.parse(sessionStorage.getItem('category7_tabContents') || '{}');
        tabContents[activeIndex] = newValue;
        sessionStorage.setItem('category7_tabContents', JSON.stringify(tabContents));
    };

    const scrollToTop = () => {
        setTimeout(() => {
            window.scrollTo(0, 0);
            if (mainContainerRef.current) mainContainerRef.current.scrollTop = 0;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 0);
    };

    // 3. Update your handleBlur function:
    const handleEditClick = () => {
        if (isUploading || isCheckingConnection) return;
        setTextareaClicked(true);

        setTimeout(() => {
            if (textareaRef.current) {
                const length = textareaRef.current.value.length;
                textareaRef.current.focus(); // prevent default scrolling
                textareaRef.current.setSelectionRange(length, length);
            }
        }, 0);
    };



    // Update handleBlur
    const handleBlur = () => {
        setKeyboardOpen(false);
        scrollToTop();
        setTextareaClicked(false);

        // comment empty હોય તો original content restore કરો
        if (!comment.trim()) {
            const original = originalContentRef.current;
            setComment(original);

            // sessionStorage પણ update કરો
            const currentData = JSON.parse(sessionStorage.getItem('friendFlowData') || '{}');
            sessionStorage.setItem('friendFlowData', JSON.stringify({ ...currentData, content: original }));
        }
    };

    // Add this ref at the top with other refs
    const scrollTimeoutRef = useRef(null);

    const handleFocus = (e) => {
        setKeyboardOpen(true);
        // Prevent default scroll behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Clear any pending scroll operations
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Scroll to top immediately to prevent keyboard push
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        // Then scroll to topRef smoothly after a brief delay
        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const element = topRef.current;
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop;

                    window.scrollTo({
                        top: targetY,
                        behavior: "smooth"
                    });
                }
            });
        }, 100);
    };

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (noInternet) return <NoInternet style={{ height: '100vh' }} t={t.noData} Nouser={currentNoNetImg} imgLoaded={noNetImgLoaded} />;
    if (error) return <NoDataFound2 style={{ height: '100vh' }} t={t.noData2} Nouser={currentNoUserImg} username={username} category={category} imgLoaded={noUserImgLoaded} />;


    return (
        <div
            style={{
                height: "100dvh",
                backgroundColor: loading || !imageLoaded ? (isDarkMode ? "#1a1a1a" : "#ffffff") : undefined,
                backgroundImage: !loading && imageLoaded ? `url(${randomData.CardBg})` : "none",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* BLUR OVERLAY */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: loading || !imageLoaded
                        ? (isDarkMode ? "rgba(26, 26, 26, 0.95)" : "rgba(255, 255, 255, 0.95)")
                        : "rgba(0, 0, 0, 0.15)",
                    backdropFilter: "blur(11px)",
                    WebkitBackdropFilter: "blur(11px)",
                    zIndex: 1,
                }}
            ></div>

            <div className="h-100" style={{ position: "relative", zIndex: 2 }}>
                <div
                    className={`mx-auto justify-content-evenly h-100`}
                    style={{
                        width: "400px",
                        maxWidth: "95vw",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {textareaClicked && (
                        <div ref={topRef}
                            className="d-flex justify-content-center align-items-center mt-2 pt-42"
                            style={{ height: '70px' }}
                        >
                            <div className="text-center" style={{ maxWidth: "80vw" }}>
                                <p
                                    className="lh-sm mx-auto ps-2 m-0"
                                    style={{
                                        fontSize: "clamp(15px, 4.5vw, 17px)",
                                        color: "#fff"
                                    }}
                                >
                                    {t?.friend?.cardTitle || "Describe your relationship anonymously"}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* STEP 1 - Top Section */}
                    <div
                        className={`d-flex flex-column flex-grow-1 ${isIOS ? "my-2" : "my-2"}`}
                        style={{
                            justifyContent: textareaClicked ? "start" : "center",
                            alignContent: textareaClicked ? "end" : "center",
                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {/* Show skeleton while loading or image not loaded */}
                        {loading || !imageLoaded ? (
                            <CardSkeleton width={cardWidth()} />
                        ) : (
                            <>
                                <div
                                    className={`rounded-4 mx-auto p-0 d-flex flex-column overflow-hidden ${showSuccessModal ? "" : "card-enter"}`}
                                    style={{
                                        width: cardWidth(),
                                        minHeight: textareaClicked ? "240px" : isSmallScreen ? "430px" : "480px",
                                        boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                        background: textareaClicked ? "rgba(255, 255, 255, 0.1)" : ``,
                                        transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                >
                                    <div
                                        className={`rounded-4 mx-auto p-0 d-flex flex-column overflow-hidden ${textareaClicked && isShaking ? "shake-animation" : ""}`}
                                        style={{
                                            border: textareaClicked ? "1px solid rgba(255, 255, 255, 0.4)" : "3px solid black",
                                            width: "100%",
                                            minHeight: textareaClicked ? "240px" : isSmallScreen ? "430px" : "480px",
                                            backgroundImage: textareaClicked ? "" : `url(${randomData.CardBg})`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                            position: 'relative',
                                            justifyContent: textareaClicked ? "start" : "space-between",
                                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        <div className='w-100 position-relative'
                                            style={{
                                                height: textareaClicked ? "110px" : "90px",
                                                background: textareaClicked ? "white" : "transparent"
                                            }}
                                        >
                                            {!textareaClicked && (
                                                <svg viewBox="0 0 315 103" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M78.1949 -41.6892C70.8197 -41.7047 74.5708 -42.9362 69.222 -39.7754C67.5907 -38.8113 62.3026 -37.2222 59.7849 -37.1517C56.0264 -37.0463 54.7974 -37.9543 51.3519 -39.3539C48.557 -40.489 50.0863 -40.656 47.4933 -41.3763C44.2546 -42.2754 45.8378 -42.0968 43.7952 -43.5277C39.1998 -42.4455 40.4518 -42.5404 35.9582 -44.2686C31.5693 -45.9562 26.1211 -43.8339 21.9635 -42.5297C20.5916 -42.0996 22.2605 -42.5001 20.9472 -42.2046C19.7103 -41.9263 19.7456 -42.0019 18.5146 -42.0565C11.5626 -42.3632 13.2644 -42.3698 10.338 -40.341C6.44774 -40.9503 8.11494 -41.8127 3.81105 -40.1379C-4.33518 -36.9679 -1.80588 -39.1873 -6.06802 -38.4616C-7.93536 -38.1431 -6.66007 -37.8128 -9.14753 -37.8938C-13.0487 -38.0206 -12.785 -38.8362 -16.0506 -37.599C-18.2473 -39.0282 -18.4561 -39.91 -21.5954 -40.7679C-25.5282 -41.8431 -26.1533 -39.8443 -29.3297 -40.9931C-30.58 -41.445 -30.5249 -41.829 -32.1819 -42.1905L-44.4734 -43.8994C-45.2947 -35.193 -44.589 9.81661 -44.5613 20.1844C-44.5425 27.3083 -45.3841 97.2458 -43.2124 101.191C-42.119 99.6041 -42.6357 98.7074 -39.6064 98.9011C-37.0581 99.0642 -38.2518 100.237 -34.0549 100.003C-29.8923 99.7709 -25.2692 98.4349 -21.7014 98.4376C-17.5112 98.4411 -17.5646 100.006 -14.1023 98.8334C-11.8693 98.0773 -13.2041 98.5729 -11.392 98.4729C-8.81594 98.3308 -8.51276 98.3889 -5.68308 98.4699C-1.11506 98.6006 -2.70242 97.5666 -0.499996 96.8537C1.371 96.8643 0.799698 99.7727 12.212 94.4782C15.9268 92.7547 12.1265 92.4868 17.8324 92.1977C24.8056 91.8441 20.4889 92.6541 24.4749 93.7813C25.726 93.0264 25.4772 93.358 25.8772 91.8461C27.3408 93.5125 28.4144 94.5508 33.0726 93.6523C34.9226 93.2955 35.6083 93.0791 36.8013 92.3803C38.9453 91.1243 37.6972 91.4628 39.1168 89.7137C39.858 91.2002 39.6298 90.961 41.6535 91.4117C42.6761 90.3944 42.2892 89.73 43.4913 88.5802C44.6768 87.4459 44.9613 87.9487 46.0157 86.225C51.4821 86.1054 48.2886 87.4758 50.0344 85.545C50.853 84.6396 51.8422 84.5761 52.173 84.4293C54.0689 83.5887 54.7133 84.393 57.0009 84.7646C60.1852 85.2821 57.9402 85.1641 60.2882 86.141C62.7001 85.0053 59.609 85.5274 63.1051 84.9265C64.124 84.751 64.0839 84.8054 65.0772 84.5622C66.3843 84.2418 69.065 83.9685 70.409 84.3265C72.6282 84.9178 72.3077 85.3796 74.0205 84.6463C75.8893 83.8464 74.8412 83.2277 77.0047 83.5106C80.1082 83.9164 77.1934 84.1568 80.0202 83.2317C82.0224 82.5766 81.8768 83.3396 83.863 83.3547C88.4535 83.3901 83.6492 82.3607 87.6194 82.3372C90.0945 82.7738 88.2529 83.3826 89.8387 83.5012C92.6774 83.714 93.1559 85.898 94.6879 86.6091C96.618 87.5054 95.3161 84.9296 99.5292 87.8723C100.944 88.8607 101.412 88.5728 102.365 89.5963L106.469 92.8409C108.675 92.7766 112.312 92.1196 113.535 92.1846C114.89 92.2565 115.612 92.7976 116.823 92.6884C118.36 92.5501 117.778 92.1922 119.332 91.572L123.548 90.1942C126.201 88.9608 128.498 90.1547 130.755 90.8192C134.189 91.8308 134.532 90.7579 136.57 90.8402C139.829 90.9724 137.949 92.0895 141.622 89.9934C148.902 85.839 146.89 88.492 152.178 90.812C155.603 89.4916 154.49 88.3542 156.663 87.5084C157.807 87.0629 157.955 87.4732 158.659 87.0984C160.057 86.3539 157.823 87.175 159.717 86.2884C161.659 86.7493 161.35 87.6872 162.814 88.1985C164.408 88.7545 164.795 87.9588 167.078 88.1271C173.75 88.6193 171.089 86.5613 174.666 85.6875C178.828 84.6712 178.326 87.2754 182.933 84.6542C183.994 84.0506 182.747 84.4174 184.677 84.0226C185.508 83.8522 186.038 83.8269 186.983 83.6278C189.564 83.0839 189.716 83.4526 190.539 81.6757L196.969 83.8079C199.256 83.9579 200.752 82.1323 204.11 83.16C207.104 84.0767 205.677 85.0382 210.558 83.1361C211.936 82.5986 212.277 82.1906 214.013 83.1199C215.753 84.0515 214.488 83.8881 215.794 85.1753L224.756 85.7787C226.027 86.1874 226.029 86.3477 226.807 86.4867C227.714 86.6487 228.342 86.5042 229.435 86.644C231.518 86.9107 231.656 87.7231 233.328 88.0552C235.967 88.5785 234.599 87.4925 236.542 89.6912C238.038 91.3852 241.744 92.6931 244.467 92.5261L249.239 92.3731C252.615 92.469 256.241 92.9009 259.012 91.8328C260.626 91.2108 261.104 90.409 262.62 89.9037C265.09 89.0806 268.875 88.3722 270.869 87.0584C272.828 85.7679 275.336 86.2793 277.342 85.2109C278.796 84.4367 279.77 82.378 280.985 80.3928C281.921 80.4874 283.086 80.6706 284.015 81.1358C285.805 82.0321 284.762 81.9179 286.007 83.1365C289.386 83.587 289.359 82.3308 292.219 82.443C294.996 82.5514 299.414 85.4426 301.757 86.9415C304.86 88.9259 304.027 87.7092 307.628 88.4725C310.358 89.0512 308.412 90.9882 317.816 92.6111C319.841 92.9607 321.715 93.3015 323.622 93.6963C324.713 93.9226 325.879 93.9104 326.824 94.1947C329.614 95.0346 329.461 96.8969 334.037 94.023C334.742 93.5804 334.99 93.1547 336.166 92.9137C337.799 92.5792 337.588 92.8648 338.832 93.3118C339.515 91.6454 340.707 90.0102 342.799 90.0361C345.996 90.0754 345.741 89.1814 347.916 87.729C351.131 85.5823 351.991 84.0748 355.601 85.1335C356.076 85.2729 356.479 85.4604 357.043 85.5166C359.613 85.7721 357.692 85.791 358.484 85.2301C359.513 83.7209 359.101 -37.1962 358.587 -43.7311C351.664 -43.8994 350.336 -40.6631 344.768 -39.4673C341.861 -38.843 338.961 -38.8891 336.349 -38.4584C334.568 -38.1644 333.414 -38.4648 331.936 -38.1594C330.211 -37.8021 330.471 -36.9882 328.822 -37.5508C328.076 -37.8053 327.227 -38.6266 325.732 -39.0284C323.99 -39.4964 323.416 -39.0425 321.678 -39.6326C318.915 -40.5705 308.243 -46.6696 302.302 -47.9985C300.408 -44.3634 300.668 -47.1059 294.992 -43.2065C289.046 -39.1218 290.295 -41.4282 286.772 -39.0625C281.156 -35.2919 274.622 -41.7425 273.252 -42.3313C270.057 -43.7044 271.522 -42.3395 269.865 -45.1699C265.095 -45.1571 264.473 -45.5162 260.871 -43.8151C257.415 -42.1831 256.109 -43.0516 253.195 -42.6056C251.739 -42.3829 246.466 -40.9538 245.693 -41.0264C243.277 -41.2532 240.73 -41.8421 238.145 -40.8762C235.905 -40.0385 237.359 -40.8016 235.071 -40.8574C233.483 -40.896 233.078 -40.0929 230.887 -40.149C227.123 -40.2454 227.156 -42.9478 223.213 -39.8718C219.68 -37.115 211.613 -39.5105 207.055 -39.1456C201.124 -38.6701 195.842 -36.3781 189.916 -37.6627C187.112 -38.2704 183.867 -37.6568 180.904 -37.471C176.491 -37.1942 173.653 -40.2063 170.51 -41.4648C169.147 -42.0108 168.477 -41.4521 166.641 -41.7724C159.511 -43.0178 160.998 -40.7815 152.33 -37.9182C146.176 -35.8851 147.912 -35.2748 145.204 -36.7253C144.618 -34.6487 143.951 -34.5691 142.117 -33.879C140.243 -33.174 141.568 -33.8199 140.315 -32.9176C137.865 -31.1528 136.9 -31.6223 134.63 -31.8474C131.435 -32.1644 132.374 -32.4182 128.996 -33.5738C127.545 -34.0705 127.447 -34.3673 126.481 -35.2103L124.054 -36.8476C122.354 -38.4416 119.448 -38.2787 117.646 -39.64C116.215 -40.7215 115.868 -42.2135 114.69 -42.9903C113.629 -43.6893 111.558 -43.9095 110.191 -44.4875C108.577 -45.1698 107.816 -46.0733 105.834 -46.5364C103.31 -47.1261 97.3803 -48.8793 95.1735 -48.5139C92.6356 -48.0936 89.3974 -45.4185 87.0087 -44.2248C84.4802 -42.961 85.2598 -43.3667 82.3399 -43.5179C78.7692 -43.7027 79.8578 -43.239 78.1949 -41.6892Z" fill="white" />
                                                    <path d="M78.1949 -41.6892C70.8197 -41.7047 74.5708 -42.9362 69.222 -39.7754C67.5907 -38.8113 62.3026 -37.2222 59.7849 -37.1517C56.0264 -37.0463 54.7974 -37.9543 51.3519 -39.3539C48.557 -40.489 50.0863 -40.656 47.4933 -41.3763C44.2546 -42.2754 45.8378 -42.0969 43.7952 -43.5277C39.1998 -42.4455 40.4518 -42.5404 35.9582 -44.2686C31.5693 -45.9562 26.1211 -43.8339 21.9635 -42.5297C20.5916 -42.0996 22.2605 -42.5001 20.9472 -42.2046C19.7103 -41.9263 19.7456 -42.0019 18.5146 -42.0565C11.5626 -42.3632 13.2644 -42.3698 10.338 -40.341C6.44774 -40.9503 8.11494 -41.8127 3.81105 -40.1379C-4.33518 -36.9679 -1.80588 -39.1873 -6.06802 -38.4616C-7.93536 -38.1431 -6.66007 -37.8128 -9.14753 -37.8938C-13.0487 -38.0206 -12.785 -38.8362 -16.0506 -37.599C-18.2473 -39.0282 -18.4561 -39.91 -21.5954 -40.7679C-25.5282 -41.8431 -26.1533 -39.8443 -29.3297 -40.9931C-30.58 -41.445 -30.5249 -41.829 -32.1819 -42.1905L-44.4734 -43.8994C-45.2947 -35.193 -44.589 9.81661 -44.5613 20.1844C-44.5425 27.3083 -45.3841 97.2458 -43.2124 101.191C-42.119 99.6041 -42.6357 98.7074 -39.6064 98.9011C-37.0581 99.0642 -38.2518 100.237 -34.0549 100.003C-29.8923 99.7709 -25.2692 98.4349 -21.7014 98.4376C-17.5112 98.4411 -17.5646 100.006 -14.1023 98.8334C-11.8693 98.0773 -13.2041 98.5729 -11.392 98.4729C-8.81594 98.3308 -8.51277 98.3889 -5.68308 98.4699C-1.11506 98.6006 -2.70241 97.5666 -0.499997 96.8537C1.371 96.8643 0.799699 99.7727 12.212 94.4782C15.9268 92.7547 12.1265 92.4868 17.8324 92.1977C24.8056 91.8441 20.4889 92.6541 24.4749 93.7813C25.726 93.0264 25.4772 93.358 25.8772 91.8461C27.3408 93.5125 28.4144 94.5508 33.0726 93.6523C34.9226 93.2955 35.6083 93.0791 36.8013 92.3803C38.9453 91.1243 37.6972 91.4628 39.1168 89.7137C39.858 91.2002 39.6298 90.961 41.6535 91.4117C42.6761 90.3944 42.2892 89.73 43.4913 88.5802C44.6769 87.4459 44.9613 87.9487 46.0157 86.225C51.4821 86.1054 48.2886 87.4758 50.0344 85.545C50.853 84.6396 51.8422 84.5761 52.173 84.4293C54.0689 83.5887 54.7133 84.393 57.0009 84.7646C60.1852 85.2821 57.9402 85.1641 60.2882 86.141C62.7001 85.0053 59.609 85.5274 63.1051 84.9265C64.124 84.751 64.0839 84.8054 65.0772 84.5622C66.3843 84.2418 69.065 83.9685 70.409 84.3265C72.6282 84.9178 72.3077 85.3796 74.0205 84.6463C75.8893 83.8464 74.8412 83.2277 77.0047 83.5106C80.1082 83.9164 77.1934 84.1568 80.0202 83.2317C82.0224 82.5766 81.8768 83.3396 83.863 83.3547C88.4535 83.3901 83.6492 82.3607 87.6194 82.3371C90.0945 82.7738 88.2529 83.3826 89.8387 83.5012C92.6774 83.714 93.1559 85.898 94.6879 86.6091C96.618 87.5054 95.3161 84.9296 99.5292 87.8723C100.944 88.8607 101.412 88.5728 102.365 89.5963L106.469 92.8409C108.675 92.7766 112.312 92.1196 113.535 92.1846C114.89 92.2565 115.612 92.7976 116.823 92.6884C118.36 92.5501 117.778 92.1922 119.332 91.572L123.548 90.1942C126.201 88.9608 128.498 90.1547 130.755 90.8192C134.189 91.8308 134.532 90.7579 136.57 90.8402C139.829 90.9724 137.949 92.0895 141.622 89.9934C148.902 85.839 146.89 88.492 152.178 90.812C155.603 89.4916 154.49 88.3542 156.663 87.5084C157.807 87.0629 157.955 87.4732 158.659 87.0984C160.057 86.3539 157.823 87.175 159.717 86.2884C161.659 86.7493 161.35 87.6872 162.814 88.1985C164.408 88.7545 164.795 87.9588 167.078 88.1271C173.75 88.6193 171.089 86.5613 174.666 85.6875C178.828 84.6712 178.326 87.2754 182.933 84.6542C183.994 84.0506 182.747 84.4174 184.677 84.0226C185.508 83.8522 186.038 83.8269 186.983 83.6278C189.564 83.0839 189.716 83.4526 190.539 81.6757L196.969 83.8079C199.256 83.9579 200.752 82.1323 204.11 83.16C207.104 84.0767 205.677 85.0382 210.558 83.1361C211.936 82.5986 212.277 82.1906 214.013 83.1199C215.753 84.0515 214.488 83.8881 215.794 85.1753L224.756 85.7787C226.027 86.1874 226.029 86.3477 226.807 86.4867C227.714 86.6487 228.342 86.5042 229.435 86.644C231.518 86.9107 231.656 87.7231 233.328 88.0552C235.967 88.5785 234.599 87.4925 236.542 89.6912C238.038 91.3852 241.744 92.6931 244.467 92.5261L249.239 92.3731C252.615 92.469 256.241 92.9009 259.012 91.8328C260.626 91.2108 261.104 90.409 262.62 89.9037C265.09 89.0806 268.875 88.3722 270.869 87.0584C272.828 85.7679 275.336 86.2793 277.342 85.2109C278.796 84.4367 279.77 82.378 280.985 80.3928C281.921 80.4874 283.086 80.6706 284.015 81.1358C285.805 82.0321 284.762 81.9179 286.007 83.1365C289.386 83.587 289.359 82.3308 292.219 82.443C294.996 82.5514 299.414 85.4426 301.757 86.9415C304.86 88.9259 304.027 87.7092 307.628 88.4725C310.358 89.0512 308.412 90.9882 317.816 92.6111C319.841 92.9607 321.715 93.3015 323.622 93.6963C324.713 93.9226 325.879 93.9104 326.824 94.1947C329.614 95.0346 329.461 96.8969 334.037 94.023C334.742 93.5804 334.99 93.1547 336.166 92.9137C337.799 92.5792 337.588 92.8648 338.832 93.3118C339.515 91.6454 340.707 90.0102 342.799 90.0361C345.996 90.0754 345.741 89.1814 347.916 87.729C351.131 85.5823 351.991 84.0748 355.601 85.1335C356.076 85.2729 356.479 85.4604 357.043 85.5166C359.613 85.7721 357.692 85.791 358.484 85.2301C359.513 83.7209 359.101 -37.1962 358.587 -43.7311C351.664 -43.8994 350.336 -40.6631 344.768 -39.4673C341.861 -38.8431 338.961 -38.8891 336.349 -38.4584C334.568 -38.1644 333.414 -38.4648 331.936 -38.1594C330.211 -37.8021 330.471 -36.9882 328.822 -37.5508C328.076 -37.8053 327.227 -38.6266 325.732 -39.0284C323.99 -39.4964 323.416 -39.0425 321.678 -39.6326C318.915 -40.5705 308.243 -46.6696 302.302 -47.9985C300.408 -44.3634 300.668 -47.1059 294.992 -43.2065C289.046 -39.1218 290.295 -41.4282 286.772 -39.0625C281.156 -35.2919 274.622 -41.7425 273.252 -42.3313C270.057 -43.7044 271.522 -42.3395 269.865 -45.1699C265.095 -45.1571 264.473 -45.5162 260.871 -43.8151C257.415 -42.1831 256.109 -43.0516 253.195 -42.6056C251.739 -42.3829 246.466 -40.9538 245.693 -41.0264C243.277 -41.2532 240.73 -41.8421 238.145 -40.8762C235.905 -40.0385 237.359 -40.8016 235.071 -40.8574C233.483 -40.896 233.078 -40.0929 230.887 -40.149C227.123 -40.2454 227.156 -42.9478 223.213 -39.8718C219.68 -37.115 211.613 -39.5105 207.055 -39.1455C201.124 -38.6701 195.842 -36.3781 189.916 -37.6627C187.112 -38.2704 183.867 -37.6568 180.904 -37.471C176.491 -37.1942 173.653 -40.2063 170.51 -41.4648C169.147 -42.0108 168.477 -41.4521 166.641 -41.7724C159.511 -43.0178 160.998 -40.7815 152.33 -37.9182C146.176 -35.8851 147.912 -35.2748 145.204 -36.7253C144.618 -34.6487 143.951 -34.5691 142.117 -33.8789C140.243 -33.174 141.568 -33.8199 140.315 -32.9176C137.865 -31.1528 136.9 -31.6223 134.63 -31.8474C131.435 -32.1644 132.374 -32.4182 128.996 -33.5738C127.545 -34.0705 127.447 -34.3673 126.481 -35.2103L124.054 -36.8476C122.354 -38.4416 119.448 -38.2787 117.646 -39.64C116.215 -40.7215 115.868 -42.2135 114.69 -42.9903C113.629 -43.6893 111.558 -43.9096 110.191 -44.4875C108.577 -45.1698 107.816 -46.0733 105.834 -46.5364C103.31 -47.1262 97.3803 -48.8793 95.1735 -48.5139C92.6356 -48.0936 89.3974 -45.4185 87.0088 -44.2248C84.4802 -42.9609 85.2598 -43.3667 82.3399 -43.5179C78.7692 -43.7027 79.8578 -43.239 78.1949 -41.6892Z" stroke="#040404" stroke-width="1.04594" stroke-miterlimit="2.61313" />
                                                </svg>
                                            )}

                                            <span
                                                className={`text-black position-absolute ${textareaClicked ? "GTMedium text-start" : "bricolage-grotesque text-center"
                                                    }`}
                                                style={{
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    fontSize: textareaClicked ? "20px" : "18px",
                                                    width: "85%",
                                                    lineHeight: "20px",
                                                    fontWeight: "500",
                                                    paddingLeft: textareaClicked ? "60px" : "",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 4,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                {textareaClicked
                                                    ? translatedName
                                                    : `${t?.common?.que || 'Que:'} ${translatedQuestion}`
                                                }                                            </span>

                                        </div>

                                        <div className='circle d-flex align-items-center justify-content-center mx-auto p-2 overflow-hidden'
                                            style={{
                                                position: textareaClicked ? "absolute" : "",
                                                top: textareaClicked ? "38px" : "0",
                                                left: textareaClicked ? "40px" : "0",
                                                margin: textareaClicked ? "" : "30px auto",
                                                transform: textareaClicked ? "translate(-50%, -50%)" : "",
                                                background: textareaClicked ? "#f2f2f2" : "white",
                                                border: "2px solid #353435",
                                                width: textareaClicked ? "55px" : isSmallScreen ? "95px" : "115px",
                                                height: textareaClicked ? "55px" : isSmallScreen ? "95px" : "115px",
                                                transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                            }}
                                        >
                                            <img src={image} alt={translatedName} style={{ width: "92%" }} />
                                        </div>


                                        {textareaClicked ? (
                                            <div className='p-2 fw-semibold text-start d-flex flex-column GTRegular' style={{ height: "100%" }}>
                                                <textarea
                                                    ref={textareaRef}
                                                    placeholder={`${t?.friend?.placeholder || "Describe your relationship"}`}
                                                    value={comment}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={handleTextChange}
                                                    onFocus={handleFocus}
                                                    onBlur={handleBlur}
                                                    className='border-0 flex-grow-1 custom-placeholder-9'
                                                    style={{
                                                        resize: 'none', backgroundColor: 'transparent',
                                                        outline: 'none', boxShadow: 'none', color: 'white',
                                                        fontSize: '17px', fontWeight: '400', lineHeight: '1.5',
                                                        caretColor: "white",
                                                    }}
                                                    enterKeyHint="done"
                                                    inputMode="text"
                                                    autoFocus
                                                />
                                                <DiceButton
                                                    category={category}
                                                    subCategory={subCategory}
                                                    onContentFetched={(text) => setComment(text)}
                                                    disabled={isUploading || isCheckingConnection}
                                                />
                                            </div>
                                        ) : (
                                            // ✅ SHOW THIS WHEN textareaClicked = false
                                            <>
                                                <div className="d-flex mt-2 align-items-center justify-content-center position-relative p-2 bg-white mb-3 mx-auto rounded-4"
                                                    style={{ minHeight: "110px", width: "90%", border: "2.5px solid black" }}>
                                                    <>
                                                        {comment ? (
                                                            <Row className="w-100 position-relative align-items-center h-100" style={{ padding: isSmallScreen ? "12px" : "16px" }}>
                                                                <Col
                                                                    xs={12}
                                                                    onClick={handleEditClick}
                                                                    className="pt-3 pe-3 cursor bricolage-grotesque"
                                                                    style={{
                                                                        fontSize: '18px',
                                                                        fontWeight: "700",
                                                                        height: 'auto',
                                                                        wordBreak: 'break-all',
                                                                        overflowWrap: 'anywhere',
                                                                        whiteSpace: 'pre-wrap',
                                                                        lineHeight: "19px",
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    {comment}
                                                                </Col>

                                                                <button
                                                                    onClick={handleEditClick}
                                                                    disabled={isUploading || isCheckingConnection}
                                                                    className="btn btn-link p-0 position-absolute"
                                                                    style={{
                                                                        bottom: "0px",
                                                                        right: "0px",
                                                                        fontSize: '18px',
                                                                        color: '#666',
                                                                        cursor: isUploading || isCheckingConnection ? 'not-allowed' : 'pointer',// ✅ ADDED
                                                                        opacity: "1"
                                                                    }}
                                                                >
                                                                    <div
                                                                        className='rounded-circle overflow-hidden d-flex justify-content-center align-items-center ms-auto'
                                                                        style={{ width: 28, height: 28, background: "#F3F3F3", border: "1px solid black" }}
                                                                    >
                                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M0.281899 10.0906L0.00598075 12.3747C-0.00693942 12.4719 0.00112984 12.5708 0.0296453 12.6647C0.0581607 12.7586 0.10646 12.8452 0.17129 12.9189C0.236121 12.9925 0.315977 13.0514 0.40548 13.0916C0.494984 13.1317 0.592056 13.1523 0.69016 13.1518C0.719384 13.1518 0.748581 13.15 0.777567 13.1463L3.06164 12.8704C3.54182 12.8095 3.98802 12.5904 4.32971 12.2476L10.2481 6.32914C10.2995 6.27776 10.3283 6.20808 10.3283 6.13542C10.3283 6.06277 10.2995 5.99309 10.2481 5.9417L7.21165 2.90249C7.16027 2.85112 7.09059 2.82227 7.01793 2.82227C6.94528 2.82227 6.8756 2.85112 6.82421 2.90249L0.904703 8.82255C0.561886 9.16424 0.342737 9.61044 0.281899 10.0906Z" fill="black" />
                                                                            <path d="M12.7366 1.83298L11.3199 0.416277C11.188 0.284306 11.0314 0.179617 10.859 0.10819C10.6866 0.0367636 10.5019 0 10.3153 0C10.1287 0 9.94396 0.0367636 9.77159 0.10819C9.59923 0.179617 9.44262 0.284306 9.31073 0.416277L7.98062 1.74638C7.92925 1.79777 7.90039 1.86746 7.90039 1.94012C7.90039 2.01278 7.92925 2.08247 7.98062 2.13385L11.019 5.17222C11.0704 5.22359 11.1401 5.25245 11.2127 5.25245C11.2854 5.25245 11.3551 5.22359 11.4065 5.17222L12.7366 3.84211C12.8685 3.71022 12.9732 3.55361 13.0447 3.38125C13.1161 3.20888 13.1528 3.02412 13.1528 2.83754C13.1528 2.65096 13.1161 2.46621 13.0447 2.29384C12.9732 2.12147 12.8685 1.96487 12.7366 1.83298Z" fill="black" />
                                                                            <path d="M13.1519 12.6046C13.1519 12.4593 13.0942 12.3199 12.9914 12.2171C12.8886 12.1144 12.7493 12.0566 12.6039 12.0566H6.30191C6.15657 12.0566 6.01718 12.1144 5.91441 12.2171C5.81164 12.3199 5.75391 12.4593 5.75391 12.6046C5.75391 12.75 5.81164 12.8894 5.91441 12.9921C6.01718 13.0949 6.15657 13.1526 6.30191 13.1526H12.6039C12.7493 13.1526 12.8886 13.0949 12.9914 12.9921C13.0942 12.8894 13.1519 12.75 13.1519 12.6046Z" fill="black" />
                                                                        </svg>

                                                                    </div>
                                                                </button>
                                                            </Row>
                                                        ) : (
                                                            <div className='w-100 d-flex justify-content-center align-items-center justify-content-center' style={{ minHeight: "70px" }}>
                                                                <textarea
                                                                    className="form-control custom-placeholder-9 border-0 text-center"
                                                                    // placeholder={`${t?.emotion?.cardPlaceholder || "Write your thoughts"}`}
                                                                    rows="1"
                                                                    style={{
                                                                        resize: 'none',
                                                                        fontSize: '16px',
                                                                        background: 'transparent',
                                                                        textAlign: 'center', // 👈 key line
                                                                    }}
                                                                    onClick={handleTextareaClick}
                                                                    readOnly
                                                                />

                                                                <p className='w-100 text-center d-flex align-items-center justify-content-center bricolage-grotesque6'
                                                                    style={{
                                                                        position: 'absolute',
                                                                        left: '0px',
                                                                        top: '55%',
                                                                        fontSize: '16px',
                                                                        transform: 'translateY(-50%)',
                                                                        pointerEvents: 'none',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        color: '#999',
                                                                    }}
                                                                >
                                                                    {t?.friend?.placeholder || "Write your thoughts"}...
                                                                    <svg width="13" height="13" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ paddingLeft: "2px" }}>
                                                                        <path d="M0.142444 5.09848L0.00302207 6.25262C-0.00350649 6.30176 0.00057091 6.35173 0.0149798 6.39917C0.0293886 6.4466 0.0537942 6.49039 0.086553 6.5276C0.119312 6.5648 0.159663 6.59456 0.204889 6.61486C0.250115 6.63516 0.299166 6.64553 0.348738 6.64527C0.363505 6.64531 0.378258 6.64438 0.392904 6.64251L1.54704 6.50308C1.78968 6.47234 2.01515 6.36161 2.1878 6.18838L5.17838 3.19781C5.20433 3.17184 5.21892 3.13663 5.21892 3.09992C5.21892 3.06321 5.20433 3.028 5.17838 3.00203L3.64405 1.46632C3.61808 1.44036 3.58287 1.42578 3.54616 1.42578C3.50945 1.42578 3.47424 1.44036 3.44828 1.46632L0.457146 4.45772C0.283921 4.63038 0.173185 4.85585 0.142444 5.09848Z" fill="#999999" />
                                                                        <path d="M6.43542 0.926203L5.71956 0.210345C5.65291 0.14366 5.57378 0.0907603 5.48668 0.0546685C5.39959 0.0185767 5.30623 0 5.21195 0C5.11767 0 5.02432 0.0185767 4.93722 0.0546685C4.85012 0.0907603 4.77099 0.14366 4.70434 0.210345L4.03224 0.882448C4.00628 0.908414 3.9917 0.943627 3.9917 0.980342C3.9917 1.01706 4.00628 1.05227 4.03224 1.07824L5.56753 2.61352C5.59349 2.63948 5.6287 2.65406 5.66542 2.65406C5.70214 2.65406 5.73735 2.63948 5.76331 2.61352L6.43542 1.94142C6.5021 1.87477 6.555 1.79564 6.59109 1.70854C6.62719 1.62145 6.64576 1.52809 6.64576 1.43381C6.64576 1.33953 6.62719 1.24618 6.59109 1.15908C6.555 1.07198 6.5021 0.992849 6.43542 0.926203Z" fill="#999999" />
                                                                        <path d="M6.64593 6.36675C6.64593 6.29331 6.61676 6.22288 6.56483 6.17095C6.5129 6.11902 6.44247 6.08984 6.36903 6.08984H3.18462C3.11118 6.08984 3.04075 6.11902 2.98882 6.17095C2.93689 6.22288 2.90771 6.29331 2.90771 6.36675C2.90771 6.44019 2.93689 6.51062 2.98882 6.56255C3.04075 6.61448 3.11118 6.64365 3.18462 6.64365H6.36903C6.44247 6.64365 6.5129 6.61448 6.56483 6.56255C6.61676 6.51062 6.64593 6.44019 6.64593 6.36675Z" fill="#999999" />
                                                                    </svg>
                                                                </p>

                                                            </div>
                                                        )}
                                                        <div
                                                            className="position-absolute"
                                                            style={{
                                                                top: "-21px",
                                                                left: "50%",
                                                                transform: "translateX(-50%)",
                                                                display: "inline-block"   // 🔥 important
                                                            }}
                                                        >
                                                            <div
                                                                className="position-relative d-inline-flex align-items-center justify-content-center px-4"
                                                                style={{
                                                                    fontWeight: "700",
                                                                    fontSize: "18px",
                                                                    height: "40px",
                                                                    minWidth: "150px",
                                                                    whiteSpace: "nowrap"
                                                                }}
                                                            >
                                                                {/* SVG Background */}
                                                                <svg
                                                                    viewBox="0 0 142 37"
                                                                    preserveAspectRatio="none"
                                                                    style={{
                                                                        position: "absolute",
                                                                        inset: 0,
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        zIndex: 0
                                                                    }}
                                                                    fill="none"
                                                                >
                                                                    <path d="M142 18.4995L138.86 19.4743C137.268 19.9683 136.14 22.3243 134.833 25.0488C133.606 27.6083 132.215 30.51 130.029 32.8306C127.638 35.3689 124.744 36.7245 121.204 36.9613V36.9955H120.42C120.302 36.9982 120.18 37 120.057 37V36.9955H21.9425V37C21.8236 37 21.7053 37 21.5877 36.9955H21.5697C17.6755 36.9137 14.5341 35.5515 11.9722 32.8306C9.78589 30.51 8.3952 27.6083 7.16815 25.0488C5.86127 22.323 4.73327 19.9686 3.1411 19.4749L0 18.4995L3.1395 17.5247C4.73183 17.031 5.85958 14.6747 7.16621 11.9505C8.39376 9.39171 9.78302 6.49 11.9708 4.16938C14.5343 1.44874 17.6752 0.0863304 21.5711 0.00451705C21.6926 0.00177223 21.8169 1.1573e-06 21.9427 1.1573e-06H120.057C120.182 1.1573e-06 120.307 1.37871e-06 120.43 0.00451705C124.324 0.0863304 127.466 1.44847 130.027 4.16938C132.216 6.49 133.606 9.39171 134.833 11.9535C136.14 14.6792 137.268 17.0335 138.86 17.5274L142 18.4995Z" fill="white" />
                                                                    <path d="M138.86 17.5269C137.268 17.0332 136.14 14.6769 134.833 11.953C133.606 9.3934 132.215 6.49177 130.029 4.17115C127.467 1.45042 124.325 0.0881003 120.432 0.00628693C120.308 0.00354211 120.184 0.00177104 120.059 0.00177104H21.9425C21.8166 0.00177104 21.6925 0.00177126 21.5707 0.00628693C17.6748 0.0881003 14.5353 1.45042 11.9703 4.17115C9.78327 6.49177 8.39376 9.3934 7.16621 11.9553C5.85966 14.682 4.73183 17.0357 3.13975 17.5297L0 18.5012L3.13975 19.4749C4.73183 19.9687 5.85966 22.3248 7.16671 25.0487C8.39376 27.6086 9.78429 30.5102 11.9708 32.8306C14.5344 35.5515 17.6752 36.9137 21.568 36.9957H21.586C21.7037 36.9982 21.8219 37 21.9408 37V36.9957H120.057V37C120.179 37 120.301 37 120.42 36.9957H121.204V36.9613C124.745 36.7244 127.638 35.3681 130.029 32.8306C132.216 30.5102 133.606 27.6086 134.834 25.0487C136.14 22.3223 137.268 19.9682 138.86 19.4743L142 18.4995L138.86 17.5269ZM133.321 24.0679C130.907 29.1057 128.171 34.8154 120.401 34.9815H21.5983C13.831 34.8154 11.0942 29.1034 8.67997 24.0679C7.5841 21.7811 6.59734 19.7228 5.24614 18.5012C6.59734 17.2817 7.5841 15.2233 8.67997 12.9348C11.0947 7.89579 13.8321 2.18461 21.6066 2.02045C21.7178 2.01771 21.831 2.01718 21.9447 2.01718H120.057C120.17 2.01718 120.283 2.01718 120.397 2.02045C128.169 2.18461 130.906 7.89579 133.321 12.9348C134.417 15.2216 135.404 17.28 136.755 18.5012C135.404 19.721 134.417 21.7801 133.321 24.0679Z" fill="#040404" />
                                                                </svg>

                                                                {/* Text */}
                                                                <span className='bricolage-grotesque'
                                                                    style={{
                                                                        position: "relative",
                                                                        zIndex: 1,
                                                                        fontWeight: "700",
                                                                        lineHeight: "15px",
                                                                        fontSize: '17px',
                                                                    }}>
                                                                    {translatedName}
                                                                </span>
                                                            </div>
                                                        </div>


                                                    </>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {textareaClicked && (
                                    <>
                                        <p className={`text-end text-white mt-2 pe-4 GTMedium mb-0`}>
                                            {convertToNativeNumerals(typedChars, lanText)}/{convertToNativeNumerals(maxLength, lanText)}
                                        </p>

                                        <BadWordWarning
                                            visible={hasBadWord}
                                            message={
                                                t?.common?.badWordWarning ||
                                                "Avoid using offensive language"
                                            }
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {!textareaClicked && (
                        <div className='mx-auto mt-auto mb-2' style={{ width: "90%" }}>
                            {loading || !imageLoaded ? (
                                // Button Skeleton
                                <div
                                    className="rounded-pill mx-auto shimmer-element"
                                    style={{
                                        width: "100%",
                                        height: "55px",
                                        transform: "scale(0.95)",
                                    }}
                                />
                            ) : (
                                <Button
                                    className="rounded-pill mx-auto GTMedium text-white border-0"
                                    style={{
                                        width: "100%",
                                        padding: "14px 1px",
                                        height: "56px",
                                        cursor: (!isUploading && comment.trim() && isCheckingConnection) ? 'pointer' : 'not-allowed',
                                        background: "black",
                                        transform: "scale(0.95)",
                                        opacity: comment.trim() ? 1 : 0.5,
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                                    }}
                                    onClick={handleSendClick}
                                    disabled={isUploading || !comment.trim() || isCheckingConnection}
                                >
                                    {isUploading || isCheckingConnection ? (
                                        <>
                                            <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                                        </>
                                    ) : (
                                        <span className='GTMedium'
                                            style={{
                                                fontSize: "clamp(21px, 4.5vw, 27px)",
                                                lineHeight: 1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {t?.annoyCard.send || "Send"}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>


            <RotatePhonePopup
                excludeWhen={[keyboardOpen]}
            />

            <LanguageDropdown subCat={subCat} />

            <CompletePopup
                showSuccessModal={showSuccessModal}
                toggle={handleModalClose}
                data={originalData}
                username={username}
                category={category}
                bg={bg}
                style={{ zIndex: 10000 }}
            />
        </div>
    );
};

export default FriendCard;